import axios from 'axios';

// Create an Axios instance with default configurations
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api', // Use environment variable or fallback to localhost
    headers: {
        'Content-Type': 'application/json',
    },
});

// Log base URL during initialization for debugging purposes
if (!process.env.REACT_APP_API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL is not defined.');
} else {
    console.log(`[Axios Initialized] Base URL: ${axiosInstance.defaults.baseURL}`);
}

// Request interceptor to optionally attach Authorization token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');

        // Define unauthenticated endpoints that should not include Authorization headers
        const unauthenticatedEndpoints = [
            '/squares',
            '/players',
            '/contact',
            '/board/grid-labels',
            '/board/status',
            '/board/add-teams',
            '/board/price',
        ];

        // Use includes to ensure query parameters or dynamic URLs are handled
        const isUnauthenticated = unauthenticatedEndpoints.some((endpoint) =>
            config.url.includes(endpoint)
        );

        // Attach Authorization header if required
        if (token && !isUnauthenticated) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        console.log(
            `[Axios Request] URL: ${config.url} | Method: ${config.method} | Auth Attached: ${
                !isUnauthenticated
            }`
        );
        return config;
    },
    (error) => {
        console.error('[Axios Request Error]', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling and token refresh
axiosInstance.interceptors.response.use(
    (response) => {
        console.log(`[Axios Response] URL: ${response.config.url} | Status: ${response.status}`);
        return response; // Pass successful responses through
    },
    async (error) => {
        if (error.response) {
            const { status, config } = error.response;

            switch (status) {
                case 401:
                    console.warn('Unauthorized access. Attempting token refresh...');
                    const refreshToken = localStorage.getItem('refreshToken');
                    if (refreshToken) {
                        try {
                            const refreshResponse = await axios.post(
                                `${process.env.REACT_APP_API_BASE_URL}/auth/refresh`,
                                { refreshToken }
                            );
                            const newToken = refreshResponse.data.token;
                            localStorage.setItem('token', newToken);

                            console.log('Token refreshed successfully. Retrying original request.');
                            config.headers.Authorization = `Bearer ${newToken}`;
                            return axiosInstance(config);
                        } catch (refreshError) {
                            console.error('Token refresh failed. Redirecting to login...', refreshError);
                        }
                    }

                    // If refresh fails or no refreshToken, redirect to login
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    window.location.href = '/login';
                    break;

                case 403:
                    console.warn('Forbidden access. You do not have the required permissions.');
                    break;

                case 404:
                    console.error(`Resource not found: ${config.url}`, error.response.data || {});
                    break;

                default:
                    console.error(
                        `HTTP Error ${status}:`,
                        error.response.data || 'Unknown error occurred.'
                    );
                    break;
            }
        } else {
            console.error(
                'Network Error or No Response:',
                error.message || 'No response received'
            );
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;